import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  ClipboardList,
  Users,
  User,
  Clock,
  CheckCircle2,
  Eye,
  Calendar
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ToolboxTalkAssignments = () => {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    try {
      const res = await axios.get(`${API}/toolbox-talk-assignments`, { withCredentials: true });
      setAssignments(res.data);
    } catch (error) {
      console.error("Error fetching assignments:", error);
      toast.error("Failed to load assignments");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed': return 'badge-success';
      case 'in_progress': return 'badge-warning';
      default: return 'badge-neutral';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="toolbox-talk-assignments">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
              Toolbox Talk Assignments
            </h1>
            <p className="text-muted-foreground mt-1">
              Track assigned toolbox talks and completion status.
            </p>
          </div>
          <Link to="/toolbox-talks">
            <Button variant="outline" data-testid="browse-talks-btn">
              Browse Talks
            </Button>
          </Link>
        </div>

        {/* Assignments List */}
        {assignments.length > 0 ? (
          <div className="space-y-4">
            {assignments.map((assignment) => {
              const talk = assignment.talk;
              const completionRate = assignment.assigned_to?.length > 0 
                ? Math.round((assignment.completion_count / assignment.assigned_to.length) * 100)
                : 0;
              
              return (
                <Card key={assignment.assignment_id} data-testid={`assignment-${assignment.assignment_id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <ClipboardList className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-primary">
                            {talk?.title || 'Toolbox Talk'}
                          </h3>
                          <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              {assignment.assignment_type === 'individual' ? (
                                <User className="w-4 h-4" />
                              ) : (
                                <Users className="w-4 h-4" />
                              )}
                              {assignment.assigned_to?.length || 0} {assignment.assignment_type === 'individual' ? 'employees' : 'groups'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {talk?.duration_minutes || 10} min
                            </span>
                            {assignment.due_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                Due: {new Date(assignment.due_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm text-muted-foreground">Completions:</span>
                            <span className="font-semibold">
                              {assignment.completion_count || 0} / {assignment.assigned_to?.length || 0}
                            </span>
                          </div>
                          <Progress value={completionRate} className="w-32 h-2" />
                        </div>
                        <Badge className={getStatusBadge(assignment.status)}>
                          {assignment.status}
                        </Badge>
                        <Link to={`/toolbox-talks/${assignment.talk_id}`}>
                          <Button variant="outline" size="sm" data-testid={`view-assignment-${assignment.assignment_id}`}>
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <ClipboardList className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="font-heading text-lg font-semibold text-primary mb-2">
                No Assignments Yet
              </h3>
              <p className="text-muted-foreground mb-4">
                {user?.role === 'employee' 
                  ? "You don't have any toolbox talks assigned yet."
                  : "Assign toolbox talks to employees or groups to get started."}
              </p>
              {(user?.role === 'super_admin' || user?.role === 'company_admin') && (
                <Link to="/toolbox-talks">
                  <Button data-testid="assign-first-btn">
                    Browse & Assign Talks
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ToolboxTalkAssignments;
